import { useUserColumns } from '@services/auth'
import { DeviceType } from '@services/constants'
import { useDevice } from '@services/device'
import { useAllSessions } from '@services/session'
import { Button } from '@shared/innmaxUI/Form'
import { Skeleton } from 'antd'
import { init, last, pathOr, toPairs } from 'ramda'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useDashboardContext } from '../../Provider'
import MainSidebar from '../MainSidebar'
import SecondarySidebar from '../SecondarySidebar'
import ConnectorCollapse from './ConnectorCollapse'

type IChargingPointSidebarProps = {}

const ChargingPointSidebar = (props: IChargingPointSidebarProps) => {
  const {
    selectedChargingPoint,
    setSelectedChargingPoint,
    currentSession,
    setCurrentSession,
    sessionsById,
    mapInstance,
  } = useDashboardContext()

  const { t } = useTranslation()

  const { getFullSessions } = useAllSessions()

  const { userColumns } = useUserColumns()

  const chargingPointShowingColumns = pathOr<Record<string, boolean>>(
    {},
    [DeviceType.CHARGINGPOINT],
    userColumns
  )

  const { data: _chargingPoint, loading: chargingPointLoading } = useDevice(
    {
      deviceType: DeviceType.CHARGINGPOINT,
      id: selectedChargingPoint?.chargingPoint.id,
    },
    {
      enabled: !!selectedChargingPoint?.chargingPoint.id,
    }
  )

  const handleSplit = (str: string, includeStr = false) => {
    const arr = str.split('.')
    for (let i = 1; i < arr.length; i++) {
      arr[i] = arr[i].charAt(0).toLowerCase() + arr[i].slice(1)
    }
    if (includeStr) {
      arr[arr.length - 1] = `${arr[arr.length - 1]}Str`
    }
    return arr
  }

  const Sidebar =
    selectedChargingPoint?.sideMenuMode === 'secondary'
      ? SecondarySidebar
      : MainSidebar

  return (
    <Sidebar
      visible={!!selectedChargingPoint}
      onClose={() => setSelectedChargingPoint(null)}
      headerMoreAction={
        selectedChargingPoint?.sideMenuMode === 'main' ? (
          <ButtonStyled
            type="primary"
            ghost
            onClick={() => {
              const sessions = getFullSessions(currentSession?.id as any) || []
              const prevSession = last(init(sessions))
              if (prevSession) {
                setCurrentSession(sessionsById[prevSession.id])
                setSelectedChargingPoint(null)
                mapInstance?.setFocusDevice(undefined)
                mapInstance?.setSelectedDevices([])
              }
            }}>
            返回一層
          </ButtonStyled>
        ) : null
      }>
      {chargingPointLoading ? (
        <div className="px-[10px]">
          <Skeleton active paragraph={{ rows: 6 }} />
        </div>
      ) : (
        <>
          <div className="px-[10px]">
            <div className="text-lg font-bold whitespace-pre-line break-words text-[#FFCC2B]">
              {_chargingPoint?.displayName}
            </div>
            <div>
              <label className="text-white">
                {pathOr(
                  '',
                  ['properties', 'chargingPointType'],
                  _chargingPoint
                )}
              </label>
            </div>
          </div>
          <ScrollContent className="overflow-auto">
            <div className="text-white text-sm flex flex-col gap-[10px]">
              <div className="whitespace-pre-line break-words">
                充電樁編號: {pathOr('', ['deviceId'], _chargingPoint)}
              </div>
              <div className="whitespace-pre-line break-words">
                充電樁名稱: {pathOr('', ['displayName'], _chargingPoint)}
              </div>
              <div className="whitespace-pre-line break-words">
                充電樁類型:
                {pathOr(
                  '',
                  ['properties', 'chargingPointType'],
                  _chargingPoint
                )}
              </div>
              <div className="whitespace-pre-line break-words">
                控制器編號:
                {pathOr('--', ['properties', 'ctrlerId'], _chargingPoint)}
              </div>
              <div className="whitespace-pre-line break-words">
                最大功率:
                {pathOr('--', ['properties', 'power'], _chargingPoint)}
              </div>
              {toPairs(chargingPointShowingColumns).map(([key, showing]) => {
                if (!showing) {
                  return null
                }
                return (
                  <div key={key} className="whitespace-pre-line break-words">
                    {t(`chargingPoint:${key}`)}:{' '}
                    {pathOr('', handleSplit(key, true), _chargingPoint) ||
                      (typeof pathOr(
                        '',
                        handleSplit(key, false),
                        _chargingPoint
                      ) === 'object'
                        ? JSON.stringify(
                            pathOr('', handleSplit(key, false), _chargingPoint)
                          )
                        : pathOr(
                            '',
                            handleSplit(key, false),
                            _chargingPoint
                          )) ||
                      pathOr('', [`${key}Str`], _chargingPoint) ||
                      (typeof pathOr('', [key], _chargingPoint) === 'object'
                        ? JSON.stringify(pathOr('', [key], _chargingPoint))
                        : pathOr('', [key], _chargingPoint))}
                  </div>
                )
              })}
            </div>
            {pathOr([], ['connectors'], _chargingPoint).map(
              (connector: any) => (
                <ConnectorCollapse
                  key={connector.connectorId}
                  connector={connector}
                />
              )
            )}
          </ScrollContent>
        </>
      )}
    </Sidebar>
  )
}

export default ChargingPointSidebar

const ScrollContent = styled.div`
  overflow-y: auto;
  /* height: calc(100% - 60px - 15px - 24px - 15px); */
  padding: 10px 10px 15px 10px;
  flex: 1;
`

const ButtonStyled = styled(Button)`
  &.ant-btn {
    padding: 0px 5px;
    height: auto;
    font-size: 14px;
  }
`
